import type { EvaluationContext } from '@openfeature/core';

export const FeatureFlag = {
  SupportChat: 'support-chat',
  IntercomTickets: 'intercom-tickets',
  // PaymentComponents picks between the new inline Chargebee Payment
  // Components checkout/setup flows (flag on) and the legacy hosted-page
  // redirect flows (flag off). The backend gates the new RPCs on the
  // same flag, so flipping it client-side without server-side rollout
  // gets you a permission-denied — the website stays on the safe path
  // until both sides see the flag.
  PaymentComponents: 'payment-components',
} as const;

export type FeatureFlag = (typeof FeatureFlag)[keyof typeof FeatureFlag];

// addonFlag mirrors the backend featureflag.AddonFlag(itemID): the GOFF flag gating a Chargebee
// addon is "Addon-<Chargebee item id>" (case-sensitive, exact). TV-1774.
export const addonFlag = (chargebeeItemId: string): string =>
  `Addon-${chargebeeItemId}`;

export type FlagUser = {
  id: string;
  email?: string;
  emailVerified?: boolean;
  username?: string;
  groups: string[];
};

export type FlagContextInput = {
  environment: string;
  region: string;
  anonId?: string;
  country?: string;
  user?: FlagUser;
};

// One context shape for the browser and the server, per ADR 20260521-feature-flags-openfeature-goff.
export const buildEvaluationContext = ({
  environment,
  region,
  anonId,
  country,
  user,
}: FlagContextInput): EvaluationContext => {
  const context: EvaluationContext = {
    targetingKey: user?.id ?? anonId ?? '',
    environment,
    region,
  };

  const upperCountry = country?.toUpperCase();

  if (user) {
    // ",a,b," lets rules match one group with `co ",a,"` and no prefix collision.
    const groups = user.groups.length > 0 ? `,${user.groups.join(',')},` : ',';

    context.user = {
      id: user.id,
      ...(user.email ? { email: user.email } : {}),
      emailVerified: user.emailVerified === true,
      username: user.username ?? '',
      groups,
      ...(upperCountry ? { country: upperCountry } : {}),
    };
  } else if (upperCountry) {
    context.user = { country: upperCountry };
  }

  return context;
};
